import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { FitText } from '@/components/FitText';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import ScannerDrawer from '@/components/ScannerDrawer';
import CategoryMenu from '@/components/CategoryMenu';
import BulkAddSheet from '@/components/BulkAddSheet';
import BulkEditDialog from '@/components/BulkEditDialog';
import BulkMoveStoreDialog from '@/components/BulkMoveStoreDialog';
import ItemEditDialog from '@/components/ItemEditDialog';
import api, { errMsg } from '@/lib/api';
import { money, STATUS_STYLES } from '@/lib/helpers';
import { toast } from 'sonner';
import { Search, ScanLine, ChevronRight, PackagePlus, TrendingUp, AlertTriangle, Layers, TableProperties, Pencil, Trash2, X, ArrowLeftRight } from 'lucide-react';

const STATUSES = ['', 'AVAILABLE', 'SOLD', 'IN_TRANSIT', 'RETURNED'];

export default function Stock() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { currentStore, stores, user, can } = useApp();
  const showCost = !!(can && can('view_cost'));
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [scoped, setScoped] = useState(true);
  const [category, setCategory] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER';

  const { data: garments = [], isLoading } = useQuery({
    queryKey: ['garments', q, status, scoped, currentStore, category, needsReview],
    queryFn: async () => (await api.get('/garments', {
      params: { q, status, category, store_id: scoped ? currentStore : '', needs_review: needsReview ? 'true' : '' },
    })).data.data,
  });

  const { data: summary = [] } = useQuery({
    queryKey: ['stock-summary'],
    queryFn: async () => (await api.get('/stock/summary')).data.data,
    refetchInterval: 30000,
  });

  const scopeRows = scoped ? summary.filter((s) => s.store_id === currentStore) : summary;
  const stockValue = scopeRows.reduce((a, s) => a + (s.stock_value || 0), 0);
  const lowStock = scopeRows.reduce((a, s) => a + (s.low_stock || 0), 0);
  const availableCount = scopeRows.reduce((a, s) => a + (s.available || 0), 0);
  const storeName = stores.find((s) => s.id === currentStore)?.name || 'Store';

  const qty = (g) => (g.status === 'AVAILABLE' ? '1 PCS' : '0 PCS');

  // ---- selection ----
  const selectedArr = useMemo(() => [...selected], [selected]);
  const selectedItems = useMemo(() => garments.filter((g) => selected.has(g.barcode)), [garments, selected]);
  const allSelected = garments.length > 0 && garments.every((g) => selected.has(g.barcode));
  const toggle = (bc) => setSelected((s) => { const n = new Set(s); n.has(bc) ? n.delete(bc) : n.add(bc); return n; });
  const toggleAll = () => setSelected((s) => {
    if (garments.every((g) => s.has(g.barcode))) return new Set();
    return new Set(garments.map((g) => g.barcode));
  });
  const clearSel = () => setSelected(new Set());
  const selectedSoldCount = garments.filter((g) => selected.has(g.barcode) && g.status !== 'AVAILABLE').length;

  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.post('/garments/bulk-delete', { barcodes: selectedArr });
      const { deleted, skipped } = r.data.data;
      toast.success(`${deleted} item(s) deleted${skipped ? ` · ${skipped} skipped (not available)` : ''}`);
      qc.invalidateQueries({ queryKey: ['garments'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      clearSel(); setDeleteOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDeleting(false); }
  };

  return (
    <Page>
      <TopBar title="Items" right={
        <Button size="sm" className="h-9 rounded-full" data-testid="stock-add-button" onClick={() => navigate('/add-stock')}>
          <PackagePlus className="h-4 w-4 lg:mr-1.5" /><span className="hidden lg:inline">Create Item</span>
        </Button>
      } />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 px-4 pt-4 lg:px-6">
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="stock-value-card">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs font-medium">Stock Value{scoped ? '' : ' · All stores'}</span>
          </div>
          <FitText className="mt-2" align="left" data-testid="stock-value-amount"><span className="font-mono text-2xl font-semibold leading-tight tabular-nums">{money(stockValue)}</span></FitText>
          <p className="mt-0.5 text-xs text-muted-foreground">On purchase price · {availableCount} items</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="low-stock-card">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-xs font-medium">Low Stock</span>
          </div>
          <div className="mt-2 font-mono text-xl font-semibold leading-tight tracking-tight tabular-nums break-words" data-testid="low-stock-count">{lowStock}</div>
          <p className="mt-0.5 text-xs text-muted-foreground">Styles running low</p>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="sticky top-14 z-30 mt-4 border-y border-border bg-background/95 px-4 py-3 backdrop-blur lg:top-16 lg:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="stock-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search code, product, colour" className="h-11 rounded-xl pl-9" />
          </div>
          <CategoryMenu value={category} onChange={setCategory} />
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="h-11 rounded-xl" data-testid="bulk-actions-trigger">
                  <Layers className="mr-1.5 h-4 w-4" /> Bulk Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem data-testid="bulk-add-open" onClick={() => setBulkAddOpen(true)}>
                  <TableProperties className="mr-2 h-4 w-4" /> Bulk Add Items
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="bulk-edit-open" disabled={selected.size === 0} onClick={() => setBulkEditOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" /> Bulk Edit{selected.size ? ` (${selected.size})` : ''}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-move-open" disabled={selected.size === 0} onClick={() => setMoveOpen(true)}>
                  <ArrowLeftRight className="mr-2 h-4 w-4" /> Move to Store{selected.size ? ` (${selected.size})` : ''}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-delete-open" disabled={selected.size === 0} onClick={() => setDeleteOpen(true)}
                  className="text-destructive focus:text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" /> Bulk Delete{selected.size ? ` (${selected.size})` : ''}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="secondary" className="h-11 w-11 rounded-xl p-0" data-testid="stock-scan" onClick={() => setScanOpen(true)}><ScanLine className="h-5 w-5" /></Button>
        </div>
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          <Chip active={scoped} onClick={() => setScoped(true)} testid="stock-scope-store">{storeName}</Chip>
          <Chip active={!scoped} onClick={() => setScoped(false)} testid="stock-scope-all">All Stores</Chip>
          <span className="w-px bg-border" />
          {STATUSES.map((s) => (
            <Chip key={s || 'all'} active={status === s} onClick={() => setStatus(s)} testid={`stock-status-${s || 'all'}`}>{s || 'All'}</Chip>
          ))}
          <span className="w-px bg-border" />
          <Chip active={needsReview} onClick={() => setNeedsReview((v) => !v)} testid="stock-needs-review">Needs review</Chip>
        </div>
      </div>

      {/* Selection bar */}
      {canManage && selected.size > 0 && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-3 py-2 lg:mx-6" data-testid="selection-bar">
          <span className="text-sm font-medium" data-testid="selection-count">{selected.size} selected</span>
          {selectedSoldCount > 0 && <span className="text-xs text-muted-foreground">({selectedSoldCount} sold — delete will skip)</span>}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" className="h-8 rounded-lg" data-testid="selection-edit" onClick={() => setBulkEditOpen(true)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
            <Button size="sm" variant="secondary" className="h-8 rounded-lg" data-testid="selection-move" onClick={() => setMoveOpen(true)}><ArrowLeftRight className="mr-1 h-3.5 w-3.5" /> Move</Button>
            <Button size="sm" variant="secondary" className="h-8 rounded-lg text-destructive" data-testid="selection-delete" onClick={() => setDeleteOpen(true)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
            <Button size="sm" variant="ghost" className="h-8 rounded-lg" data-testid="selection-clear" onClick={clearSel}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden px-6 py-4 lg:block">
        {isLoading ? <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p> :
          garments.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No items found.</p> :
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <Table data-testid="stock-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  {canManage && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="stock-select-all" aria-label="Select all" /></TableHead>}
                  <TableHead>Item Name</TableHead>
                  <TableHead>Item Code</TableHead>
                  {!scoped && <TableHead>Store</TableHead>}
                  <TableHead>Stock QTY</TableHead>
                  <TableHead className="text-right">Selling Price</TableHead>
                  {showCost && <TableHead className="text-right">Purchase Price</TableHead>}
                  <TableHead className="text-right">MRP</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {garments.map((g) => (
                  <TableRow key={g.id} data-testid={`stock-row-${g.barcode}`} data-state={selected.has(g.barcode) ? 'selected' : undefined}
                    className="cursor-pointer" onClick={() => navigate(`/garment/${g.barcode}`)}>
                    {canManage && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.has(g.barcode)} onCheckedChange={() => toggle(g.barcode)} data-testid={`stock-select-${g.barcode}`} />
                      </TableCell>
                    )}
                    <TableCell>
                      <p className="font-medium">{g.product || '(no name)'}</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        {g.category ? <Badge variant="secondary" className="rounded-md text-[10px] font-medium uppercase">{g.category}</Badge> : null}
                        {g.colour ? <span className="text-xs text-muted-foreground">{g.colour}{g.size ? ` · ${g.size}` : ''}</span> : (g.size ? <span className="text-xs text-muted-foreground">{g.size}</span> : null)}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{g.barcode}</TableCell>
                    {!scoped && <TableCell className="text-sm text-muted-foreground">{stores.find((s) => s.id === g.store_id)?.name || '-'}</TableCell>}
                    <TableCell>
                      <span className="font-medium">{qty(g)}</span>
                      <span className={`ml-2 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100'}`}>{g.status}</span>
                      {g.needs_review && <span className="ml-1 inline-block rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-medium text-sky-700" data-testid={`stock-review-${g.barcode}`}>NEEDS COST</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(g.price)}</TableCell>
                    {showCost && <TableCell className="text-right font-mono tabular-nums">{g.cost != null ? money(g.cost) : '-'}</TableCell>}
                    <TableCell className="text-right text-muted-foreground">-</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canManage && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 rounded-lg p-0" data-testid={`stock-edit-${g.barcode}`}
                            onClick={() => setEditItem(g)} aria-label="Edit item"><Pencil className="h-4 w-4" /></Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>}
      </div>

      {/* Mobile card list */}
      <div className="px-4 py-3 lg:hidden">
        {isLoading ? <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p> :
          garments.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No garments found.</p> :
          <div className="space-y-2" data-testid="stock-list">
            {garments.map((g) => (
              <div key={g.id} className="flex items-center gap-2 rounded-xl border border-border bg-card p-3">
                {canManage && <Checkbox checked={selected.has(g.barcode)} onCheckedChange={() => toggle(g.barcode)} data-testid={`stock-mselect-${g.barcode}`} />}
                <button data-testid={`stock-item-${g.barcode}`} onClick={() => navigate(`/garment/${g.barcode}`)}
                  className="flex flex-1 items-center justify-between gap-3 text-left">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{g.product}</p>
                    <p className="font-mono text-xs text-muted-foreground">{g.barcode} · {g.size || '-'} · {g.colour || '-'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold tabular-nums">{money(g.price)}</p>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100 text-slate-700'}`}>{g.status}</span>
                      {g.needs_review && <span className="ml-1 inline-block rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700">NEEDS COST</span>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
                {canManage && (
                  <Button size="sm" variant="ghost" className="h-9 w-9 shrink-0 rounded-lg p-0" data-testid={`stock-medit-${g.barcode}`}
                    onClick={() => setEditItem(g)} aria-label="Edit item"><Pencil className="h-4 w-4" /></Button>
                )}
              </div>
            ))}
          </div>}
      </div>

      <ScannerDrawer open={scanOpen} onOpenChange={setScanOpen} onDetected={(code) => { setScanOpen(false); navigate(`/garment/${String(code).trim().toUpperCase()}`); }} />
      <BulkAddSheet open={bulkAddOpen} onOpenChange={setBulkAddOpen} onDone={clearSel} />
      <BulkEditDialog open={bulkEditOpen} onOpenChange={setBulkEditOpen} barcodes={selectedArr} onDone={clearSel} />
      <BulkMoveStoreDialog open={moveOpen} onOpenChange={setMoveOpen} items={selectedItems} onDone={clearSel} />
      <ItemEditDialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }} garment={editItem} onDone={() => setEditItem(null)} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} selected item{selected.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Only AVAILABLE items are removed. {selectedSoldCount > 0 ? `${selectedSoldCount} sold item(s) will be kept.` : ''} This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="bulk-delete-confirm">
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`}>{children}</button>
);
